import { useState, useCallback } from 'react';
import { 
  withRetry, 
  getUserFriendlyErrorMessage, 
  logError, 
  RetryOptions,
  defaultRetryOptions 
} from '../utils/errorHandling';

interface ErrorState {
  error: string | null;
  isRetrying: boolean;
  retryCount: number;
}

interface UseErrorHandlerReturn {
  error: string | null;
  isRetrying: boolean;
  retryCount: number;
  handleError: (error: unknown, context?: string) => void;
  clearError: () => void;
  executeWithRetry: <T>(
    operation: () => Promise<T>,
    options?: Partial<RetryOptions>
  ) => Promise<T>;
  retry: (operation: () => Promise<void>) => Promise<void>;
}

export const useErrorHandler = (): UseErrorHandlerReturn => {
  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    isRetrying: false,
    retryCount: 0
  });

  const handleError = useCallback((error: unknown, context?: string) => {
    const friendlyMessage = getUserFriendlyErrorMessage(error);
    
    setErrorState(prev => ({
      ...prev,
      error: friendlyMessage,
      isRetrying: false
    }));
    
    logError(error, context);
  }, []);

  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      isRetrying: false,
      retryCount: 0
    });
  }, []);

  const executeWithRetry = useCallback(async <T>(
    operation: () => Promise<T>,
    options: Partial<RetryOptions> = {}
  ): Promise<T> => {
    const config = { ...defaultRetryOptions, ...options };
    
    setErrorState(prev => ({
      ...prev,
      error: null,
      isRetrying: true,
      retryCount: 0
    }));

    try {
      const result = await withRetry(operation, {
        ...config,
        maxAttempts: config.maxAttempts
      });
      
      setErrorState(prev => ({
        ...prev,
        isRetrying: false
      }));
      
      return result;
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, [handleError]);

  const retry = useCallback(async (operation: () => Promise<void>) => {
    setErrorState(prev => ({
      ...prev,
      error: null,
      isRetrying: true,
      retryCount: prev.retryCount + 1
    }));

    try {
      await operation();
      setErrorState(prev => ({
        ...prev,
        isRetrying: false
      }));
    } catch (error) {
      handleError(error);
    }
  }, [handleError]);

  return {
    error: errorState.error,
    isRetrying: errorState.isRetrying,
    retryCount: errorState.retryCount,
    handleError,
    clearError,
    executeWithRetry,
    retry
  };
};